module Client.Page.User.SensorMap

open System
open Client
open Client.Components
open Client.Domain
open Client.DomainTypes.SensorMap
open Client.Infrastructure
open Client.Map.SensorPopup
open Client.Map.MapMarker
open Elmish
open Fable.FontAwesome
open Fable.React.Props
open Browser
open Fulma
open Fable.React
open Leaflet
open ReactLeaflet
open Client.Infrastructure.Api
open Client.DomainTypes.Msg
open Shared.Dto
open Shared.Dto.Dto
open Thoth.Elmish
open Shared.Dto.MapSensorData

let private latLngToLocation (latLng: LatLng) : Location = {
    Latitude = latLng.lat
    Longitude = latLng.lng
}

type MapViewData = {
    Center: Location
    Zoom: float
}

type Model = {
    Session: SessionKey
    Sensors: MapSensor list
    LastUpdate: DateTimeOffset option
    MapViewData: MapViewData
    RefreshRunning: bool
}

let onDrag (dispatch: MapMsg -> Unit) (map: Leaflet.Map) =
    let newCenter = map.getCenter () |> latLngToLocation

    dispatch (MapMoved newCenter)

let private getMapFromEvent (event: LeafletEvent) =
    event.target |> Option.map (fun target -> target :?> Leaflet.Map) |> Option.get


let onZoom (dispatch: MapMsg -> Unit) (map: Leaflet.Map) =
    let data: MapZoomedData = {
        Center = map.getCenter () |> latLngToLocation
        Zoom = map.getZoom ()
    }

    dispatch (MapZoomed data)

let view (model: Model) (dispatch: Msg -> Unit) =
    let markers =
        model.Sensors
        |> List.map (fun sensor ->
            let position = makePosition sensor.Latitude sensor.Longitude

            let popupContent = makeMarkerPopup sensor dispatch
            makeMarker sensor.Data position popupContent
        )

    [
        map
            [
                MapProps.MinZoom 6.
                MapProps.MaxZoom 19.
                MapProps.Center(model.MapViewData.Center |> Location.toLatLngExpression)
                MapProps.Zoom model.MapViewData.Zoom
                MapProps.ZoomControl true
                MapProps.Style [
                    Height "calc(100vh - 3.25rem)"
                    ZIndex "0"
                    FlexGrow 1
                ]
                MapProps.OnDragEnd(getMapFromEvent >> onDrag (Map >> dispatch))
                MapProps.OnZoomEnd(getMapFromEvent >> onZoom (Map >> dispatch))
            ]
            (tileLayer [
                TileLayerProps.Url "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
             ] []
             :: markers)

        Button.button [
            Button.IsLoading model.RefreshRunning
            Button.Color Color.IsLink
            Button.Props [
                Style [
                    Position PositionOptions.Absolute
                    Bottom "10px"
                    Right "10px"
                    ZIndex "1"
                ]
            ]
            Button.OnClick(fun _ -> dispatch (Map MapMsg.RefreshData))
        ] [
            Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.Sync ] [] ]
            span [] [ str "Aktualisieren" ]
        ]
    ]

let storeMapDataInLocalStorage (data: MapViewData) = LocalStorage.saveJson "map-data" data

let retrieveMapDataFromLocalStorage () =
    LocalStorage.loadJson<MapViewData> "map-data"


let retrieveSensorsCmd sessionKey =
    Cmd.OfAsync.either api.getSensors sessionKey MapMsg.GotSensors MapMsg.SensorLoadingFailed

let private getInitialMapViewData (maybeExistingData: MapViewData option) =
    maybeExistingData
    |> Option.defaultValue {
        Zoom = 8.
        Center = {
            Latitude = 47.59397
            Longitude = 14.12456
        }
    }

let init (session: UserSession) =
    let maybeExistingViewData = retrieveMapDataFromLocalStorage ()

    let mapViewData = getInitialMapViewData maybeExistingViewData

    storeMapDataInLocalStorage mapViewData

    let model = {
        Sensors = []
        Session = session.SessionKey
        LastUpdate = None
        RefreshRunning = true
        MapViewData = mapViewData
    }

    let cmd = retrieveSensorsCmd session.SessionKey

    model, cmd

let updateSensors (sensors: MapSensor list) =
    let dates =
        sensors
        |> List.map (fun sensor -> sensor.Data)
        |> List.map getSensorDataDate
        |> List.choose id

    let latestDate =
        match dates with
        | x :: _ -> Some(List.max dates)
        | _ -> None

    latestDate

let update (msg: MapMsg) (model: Model) =
    match msg with
    | MapMsg.GotSensors sensorsResult ->
        match sensorsResult with
        | Result.Ok sensors ->
            let latestDate = updateSensors sensors

            let toastCmd = Toast.create "Daten erfolgreich geladen" |> Toast.success

            {
                model with
                    Sensors = sensors
                    LastUpdate = latestDate
                    RefreshRunning = false
            },
            toastCmd
        | Result.Error error ->
            let config: ErrorConfirmation.Config<MapMsg> = {
                Handler = ErrorConfirmation.createHandler MapMsg.ReloadPage
                TimeoutMilliseconds = 5000
            }

            let cmd = ErrorConfirmation.responseError config error

            { model with RefreshRunning = false }, cmd
    | MapMsg.MapMoved center ->
        let newMapViewData = { model.MapViewData with Center = center }

        storeMapDataInLocalStorage newMapViewData

        { model with MapViewData = newMapViewData }, Cmd.none
    | MapMsg.MapZoomed data ->
        let newMapViewData = {
            model.MapViewData with
                Zoom = data.Zoom
                Center = data.Center
        }

        storeMapDataInLocalStorage newMapViewData

        { model with MapViewData = newMapViewData }, Cmd.none
    | MapMsg.MoveToLocation location ->
        let newMapViewData = {
            Zoom = 14.
            Center = location
        }

        storeMapDataInLocalStorage newMapViewData

        { model with MapViewData = newMapViewData }, Cmd.none
    | MapMsg.RefreshData ->
        let cmd = retrieveSensorsCmd model.Session

        { model with RefreshRunning = true }, cmd
    | MapMsg.SensorLoadingFailed ex ->
        let config: ErrorConfirmation.Config<MapMsg> = {
            Handler = ErrorConfirmation.createHandler MapMsg.ReloadPage
            TimeoutMilliseconds = 5000
        }

        let cmd = ErrorConfirmation.dataTransmissionErrorConfirmation config ex

        { model with RefreshRunning = false }, cmd
    | MapMsg.ReloadPage reload ->
        if reload then window.location.reload true else ()

        model, Cmd.none